import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { TicketForm } from "@/components/PositionDrawer";
import PlanMarkerPicker from "@/components/PlanMarkerPicker";
import MediaUploader from "@/components/MediaUploader";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";

const NONE = "__none";
const NEW = "__new";

export default function NewTicketDialog({ open, onClose, onCreated }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [positions, setPositions] = useState(null);
  const [positionId, setPositionId] = useState("");
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [marker, setMarker] = useState(null);
  const [created, setCreated] = useState(null);
  const [uploads, setUploads] = useState(0);

  useEffect(() => {
    if (open) { api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => setProjects([])); }
  }, [open]);
  useEffect(() => {
    setPositionId(""); setPositions(null); setPlanId(""); setPlans([]);
    if (projectId) {
      api.get(`/projects/${projectId}/positions`).then((r) => setPositions(r.data || [])).catch(() => setPositions([]));
      api.get("/overview/plans").then((r) => setPlans((r.data || []).filter((p) => p.project_id === projectId))).catch(() => setPlans([]));
    }
  }, [projectId]);
  const planPositions = (positions || []).filter((p) => planId && planId !== NONE && p.plan_id === planId);
  const withoutPosition = planId === NONE || positionId === NONE;
  const newMarker = positionId === NEW;
  const ready = projectId && (withoutPosition || (newMarker ? !!marker : positionId));
  const resolvePosition = async (title) => (await api.post("/positions", { plan_id: planId, marker, title: title || "" })).data.id;

  const close = () => { setProjectId(""); setPositionId(""); setPlanId(""); setMarker(null); setCreated(null); setUploads(0); onClose(); };

  if (open && created) {
    return (
      <Dialog open onOpenChange={(o) => { if (!o) { onCreated(); close(); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-ticket-photos-dialog">
          <DialogHeader><DialogTitle>Fotos zum Ticket</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="new-ticket-created"><CheckCircle2 className="h-4 w-4" />{created.ticket_number || "Ticket"} · {created.title} wurde erstellt.</div>
          <p className="text-sm text-slate-500">Nehmen Sie jetzt Fotos auf oder laden Sie Bilder hoch (optional).</p>
          <MediaUploader links={created.position_id ? { ticket_id: created.id, position_id: created.position_id } : { ticket_id: created.id, project_id: created.project_id }} onUploaded={() => setUploads((n) => n + 1)} />
          {uploads > 0 && <div className="text-xs text-slate-500" data-testid="new-ticket-upload-count">{uploads} Upload(s) gespeichert</div>}
          <Button onClick={() => { onCreated(); close(); }} data-testid="new-ticket-done">Fertig</Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-ticket-dialog">
        <DialogHeader><DialogTitle>Neues Ticket erstellen</DialogTitle></DialogHeader>
        <div className="grid gap-2">
          <div><Label className="text-xs">Projekt *</Label>
            <Select value={projectId} onValueChange={(v) => { if (v) { setProjectId(v); } }}>
              <SelectTrigger data-testid="new-ticket-project"><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Bauplan</Label>
            <Select value={planId} onValueChange={(v) => { if (v) { setPlanId(v); setPositionId(""); setMarker(null); } }} disabled={!projectId}>
              <SelectTrigger data-testid="new-ticket-plan"><SelectValue placeholder="Bauplan wählen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— ohne Bauplan / Position —</SelectItem>
                {plans.map((p) => <SelectItem key={p.id} value={p.id}>{[p.building_name, p.floor_name, p.name].filter(Boolean).join(" · ")} ({p.position_count || 0} Pos.)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Position</Label>
            <Select value={positionId} onValueChange={(v) => { if (v) { setPositionId(v); } }} disabled={!planId || planId === NONE}>
              <SelectTrigger data-testid="new-ticket-position"><SelectValue placeholder={planId && planId !== NONE && !planPositions.length ? "Keine Positionen" : "Position wählen"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— ohne Position —</SelectItem>
                <SelectItem value={NEW}>＋ Neuen Marker im Plan setzen</SelectItem>
                {planPositions.map((p) => <SelectItem key={p.id} value={p.id}>{p.number}{p.room ? ` · ${p.room}` : ""}{p.title ? ` · ${p.title}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {withoutPosition && <p className="text-xs text-slate-500" data-testid="new-ticket-no-position-hint">Das Ticket wird direkt am Projekt angelegt, ohne Marker im Bauplan.</p>}
        {newMarker && <PlanMarkerPicker planId={planId} value={marker} onChange={setMarker} />}
        {ready && <TicketForm key={`${projectId}-${positionId || "none"}`} positionId={withoutPosition || newMarker ? "" : positionId} projectId={projectId} resolvePosition={newMarker ? resolvePosition : undefined} onSaved={(t) => { if (t?.id) { setCreated(t); } else { onCreated(); close(); } }} onCancel={close} />}
      </DialogContent>
    </Dialog>
  );
}
